import {
	ApiDatabaseViewAssignmentResponse,
	ApiDatabaseViewInstitutionResponse,
	ApiDatabaseViewLekResponse,
	ApiDatabaseViewResponseObject,
	ApiDatabaseViewSchema,
	ApiDatabaseViewSponsorResponse,
} from '../api-types/database-view.api';
import {
	DatabaseViewAssignmentResponse,
	DatabaseViewInstitutionResponse,
	DatabaseViewLekResponse,
	DatabaseViewResponseObject,
	DatabaseViewSchema,
	DatabaseViewSponsorResponse,
} from '../app-types/database-view.app';

export const transformApiDatabaseViewSchemaToApp = (input: ApiDatabaseViewSchema): DatabaseViewSchema => ({
	view: input.view,
	version: input.version,
	columns: input.columns.map((column) => ({ ...column })),
});

export const transformApiDatabaseViewResponseObjectToApp = (
	input: ApiDatabaseViewResponseObject | null | undefined,
): DatabaseViewResponseObject | null => {
	if (!input) return null;
	return {
		id: input.id,
		text: input.text,
		guid: input.guid,
	};
};

export const transformApiDatabaseViewAssignmentResponseToApp = (
	input: ApiDatabaseViewAssignmentResponse,
): DatabaseViewAssignmentResponse => {
	return {
		client: transformApiDatabaseViewResponseObjectToApp(input.client),
		phase: transformApiDatabaseViewResponseObjectToApp(input.phase),
		assignment: transformApiDatabaseViewResponseObjectToApp(input.assignment),
		typeOfCare: transformApiDatabaseViewResponseObjectToApp(input.typeOfCare),
		initiator: transformApiDatabaseViewResponseObjectToApp(input.initiator),
		caseGroup: transformApiDatabaseViewResponseObjectToApp(input.caseGroup),
		legalFoundation: transformApiDatabaseViewResponseObjectToApp(input.legalFoundation),
		integrationServiceOffice: transformApiDatabaseViewResponseObjectToApp(input.integrationServiceOffice),
		service: transformApiDatabaseViewResponseObjectToApp(input.service),
		advisor: transformApiDatabaseViewResponseObjectToApp(input.advisor),
		assignmentSponsor: transformApiDatabaseViewResponseObjectToApp(input.assignmentSponsor),
		categorySponsor: transformApiDatabaseViewResponseObjectToApp(input.categorySponsor),
		careRelatedHandicap: transformApiDatabaseViewResponseObjectToApp(input.careRelatedHandicap),
		startOfConsultation: input.startOfConsultation,
		startOfCare: input.startOfCare,
		cancellationDate: input.cancellationDate,
		reportDueDate: input.reportDueDate,
		assignmentNumber: input.assignmentNumber,
		institutionAtStart: input.institutionAtStart ?? null,
		institutionAtDate: input.institutionAtDate ?? null,
		institutionAtEnd: input.institutionAtEnd ?? null,
		schoolNumberAtStart: input.schoolNumberAtStart ?? null,
		schoolNumberAtDate: input.schoolNumberAtDate ?? null,
		schoolNumberAtEnd: input.schoolNumberAtEnd ?? null,
		socialSecurityStatusAtStart: input.socialSecurityStatusAtStart ?? null,
		socialSecurityStatusAtDate: input.socialSecurityStatusAtDate ?? null,
		socialSecurityStatusAtEnd: input.socialSecurityStatusAtEnd ?? null,
		disabilityTypeAtStart: input.disabilityTypeAtStart ?? null,
		disabilityTypeAtDate: input.disabilityTypeAtDate ?? null,
		disabilityTypeAtEnd: input.disabilityTypeAtEnd ?? null,
		disabilityProofAtStart: input.disabilityProofAtStart ?? null,
		disabilityProofAtDate: input.disabilityProofAtDate ?? null,
		disabilityProofAtEnd: input.disabilityProofAtEnd ?? null,
		gdbAtStart: input.gdbAtStart ?? null,
		gdbAtDate: input.gdbAtDate ?? null,
		gdbAtEnd: input.gdbAtEnd ?? null,
		lastThreeContactDates: input.lastThreeContactDates ?? null,
		contactTaskCount: input.contactTaskCount ?? null,
		impedimentsAtStart: input.impedimentsAtStart ?? [],
		impedimentsAtEnd: input.impedimentsAtEnd ?? [],
	};
};

export const transformApiDatabaseViewAssignmentResponsesToApp = (
	input: ApiDatabaseViewAssignmentResponse[],
): DatabaseViewAssignmentResponse[] => {
	return input.map(transformApiDatabaseViewAssignmentResponseToApp);
};

export const transformApiDatabaseViewInstitutionResponseToApp = (
	input: ApiDatabaseViewInstitutionResponse,
): DatabaseViewInstitutionResponse => {
	return {
		institutionBranch: transformApiDatabaseViewResponseObjectToApp(input.institutionBranch),
		mainInstitution: transformApiDatabaseViewResponseObjectToApp(input.mainInstitution),
		category: transformApiDatabaseViewResponseObjectToApp(input.category),
		address: transformApiDatabaseViewResponseObjectToApp(input.address),
		schoolNumber: input.schoolNumber,
		phoneNumber: input.phoneNumber,
		email: input.email,
		advisorCount: input.advisorCount,
		activeClientsCount: input.activeClientsCount,
	};
};

export const transformApiDatabaseViewInstitutionResponsesToApp = (
	input: ApiDatabaseViewInstitutionResponse[],
): DatabaseViewInstitutionResponse[] => {
	return input.map(transformApiDatabaseViewInstitutionResponseToApp);
};

export const transformApiDatabaseViewLekResponseToApp = (
	input: ApiDatabaseViewLekResponse,
): DatabaseViewLekResponse => {
	return {
		assistanceResultId: input.assistanceResultId ?? 0,
		assistanceResult: input.assistanceResult ?? null,
		clientId: input.clientId ?? 0,
		clientName: input.clientName ?? null,
		startDate: input.startDate ?? null,
		endDate: input.endDate ?? null,
		duration: input.duration ?? null,
		categorySponsor: input.categorySponsor ?? null,
		assignmentId: input.assignmentId ?? 0,
		phase: input.phase ?? null,
		typeOfCare: input.typeOfCare ?? null,
		startOfCare: input.startOfCare ?? null,
		cancellationDate: input.cancellationDate ?? input.deregistrationDate ?? null,
		caseGroup: input.caseGroup ?? input.categoryCaseGroup ?? null,
		legalFoundation: input.legalFoundation ?? null,
		initiator: input.initiator ?? null,
		integrationServiceOfficeId: input.integrationServiceOfficeId ?? 0,
		integrationServiceOffice: input.integrationServiceOffice ?? null,
		service: input.service ?? null,
		advisorId: input.advisorId ?? 0,
		advisorGuid: input.advisorGuid ?? '',
		advisorName: input.advisorName ?? null,
		disabilityType: input.disabilityType ?? null,
		assignmentNumber: input.assignmentNumber ?? null,
		institutionAtStart: input.institutionAtStart ?? null,
		institutionAtDate: input.institutionAtDate ?? null,
		institutionAtEnd: input.institutionAtEnd ?? null,
		schoolNumberAtStart: input.schoolNumberAtStart ?? null,
		schoolNumberAtDate: input.schoolNumberAtDate ?? null,
		schoolNumberAtEnd: input.schoolNumberAtEnd ?? null,
		socialSecurityStatusAtStart: input.socialSecurityStatusAtStart ?? null,
		socialSecurityStatusAtDate: input.socialSecurityStatusAtDate ?? null,
		socialSecurityStatusAtEnd: input.socialSecurityStatusAtEnd ?? null,
		disabilityTypeAtStart: input.disabilityTypeAtStart ?? null,
		disabilityTypeAtDate: input.disabilityTypeAtDate ?? null,
		disabilityTypeAtEnd: input.disabilityTypeAtEnd ?? null,
		disabilityProofAtStart: input.disabilityProofAtStart ?? null,
		disabilityProofAtDate: input.disabilityProofAtDate ?? null,
		disabilityProofAtEnd: input.disabilityProofAtEnd ?? null,
		gdbAtStart: input.gdbAtStart ?? null,
		gdbAtDate: input.gdbAtDate ?? null,
		gdbAtEnd: input.gdbAtEnd ?? null,
		assignmentSponsor: input.assignmentSponsor ?? null,
		lastThreeContactDates: input.lastThreeContactDates ?? null,
		contactTaskCount: input.contactTaskCount ?? null,
		impedimentsAtStart: input.impedimentsAtStart ?? [],
		impedimentsAtEnd: input.impedimentsAtEnd ?? [],
	};
};

export const transformApiDatabaseViewLekResponsesToApp = (
	input: ApiDatabaseViewLekResponse[],
): DatabaseViewLekResponse[] => {
	return input.map(transformApiDatabaseViewLekResponseToApp);
};

export const transformApiDatabaseViewSponsorResponseToApp = (
	input: ApiDatabaseViewSponsorResponse,
): DatabaseViewSponsorResponse => {
	return {
		id: input.id,
		name: input.name,
		initial: input.initial,
	};
};

export const transformApiDatabaseViewSponsorResponsesToApp = (
	input: ApiDatabaseViewSponsorResponse[],
): DatabaseViewSponsorResponse[] => {
	return input.map(transformApiDatabaseViewSponsorResponseToApp);
};
